import type * as React from "react";
import { useEffect, useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

// UI
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { getProperty, editFinance } from "@/services/finance";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { FileUpload } from "@/components/FileUpload";

// Icons
import { Save } from "lucide-react";

export const Route = createFileRoute(
	"/_layout-private/report/finance/company/_layout/finance-property/edit",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const companyId = localStorage.getItem("companyId"); // Get companyId from local storage
	const yearly = Number(localStorage.getItem("year")); // Get year from local storage
	const [docFile, setDocFile] = useState("");

	const [revolvingAssets, setRevolvingAssets] = useState<
		Array<{
			name: string;
			amount: string; // Change to string type
			remark: string;
		}>
	>([]);

	const [nonRevolvingAssets, setNonRevolvingAssets] = useState<
		Array<{
			name: string;
			amount: string; // Change to string type
			remark: string;
		}>
	>([]);

	// Query
	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["property", companyId],
		queryFn: () => getProperty(companyId as string, yearly),
	});

	// Mutations
	const mutation = useMutation({
		mutationFn: (data: any) => editFinance(companyId as string, data),
		onSuccess: () => {
			toast({
				title: "ບັນທຶກສຳເລັດ",
				description: "ແກ້ໄຂຂໍ້ມູນສຳເລັດ",
				variant: "success",
			});
			queryClient.invalidateQueries({
				queryKey: ["property", companyId, yearly],
			});
			navigate({ to: "/report/finance/company/finance-property" });
		},
		onError: (error) => {
			toast({
				title: "ບັນທຶກລົ້ມເຫຼວ",
				description: "ບໍ່ສາມາດແກ້ໄຂຂໍ້ມູນໄດ້",
				variant: "destructive",
			});
			console.log("1111111111111111", error);
		},
	});

	// Effects
	useEffect(() => {
		if (data) {
			setDocFile(data?.docFile || "");

			const formatInitialAmount = (amount: number) => {
				if (amount < 0) {
					return `(${Math.abs(amount)})`;
				}
				return String(amount);
			};

			const revolvingAssets =
				data?.assetDetails?.filter(
					(asset: any) => asset.assetType === "REVOLVING",
				) || [];
			const nonRevolvingAssets =
				data?.assetDetails?.filter(
					(asset: any) => asset.assetType === "NON_REVOLVING",
				) || [];

			setRevolvingAssets(
				revolvingAssets.map((asset: any) => ({
					name: asset.name,
					amount: formatInitialAmount(asset.amount),
					remark: asset.remark,
				})),
			);

			setNonRevolvingAssets(
				nonRevolvingAssets.map((asset: any) => ({
					name: asset.name,
					amount: formatInitialAmount(asset.amount),
					remark: asset.remark,
				})),
			);
		}
	}, [data]);

	if (isLoading) return <DinamixSkeletonTable />;
	if (isError) return <div>Error: {error.message}</div>;

	// Functions

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const convertAmount = (amount: string) => {
			if (!amount) return 0;
			const cleanValue = amount.replace(/[,()]/g, "");
			const numValue = Number(cleanValue);
			return amount.includes("(") ? -numValue : numValue;
		};

		if (!docFile) {
			toast({
				title: "ບັນທຶກລົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
				variant: "destructive",
			});
			return;
		}

		const data = {
			companyId,
			yearly: yearly,
			docFile: docFile,
			assetDetails: [
				...revolvingAssets.map((asset) => ({
					...asset,
					amount: convertAmount(asset.amount),
					assetType: "REVOLVING" as const,
				})),
				...nonRevolvingAssets.map((asset) => ({
					...asset,
					amount: convertAmount(asset.amount),
					assetType: "NON_REVOLVING" as const,
				})),
			],
		};

		mutation.mutate(data);
	};

	// Add total calculation functions
	const calculateRevolvingTotal = () => {
		return revolvingAssets.reduce((sum, asset) => {
			if (!asset.amount) return sum;

			// Remove commas and handle parentheses
			const cleanValue = asset.amount.replace(/[,()]/g, "");
			const numValue = Number(cleanValue);

			if (isNaN(numValue)) return sum;

			// If the amount is in parentheses, treat it as negative
			return sum + (asset.amount.includes("(") ? -numValue : numValue);
		}, 0);
	};

	const calculateNonRevolvingTotal = () => {
		return nonRevolvingAssets.reduce((sum, asset) => {
			if (!asset.amount) return sum;

			// Remove commas and handle parentheses
			const cleanValue = asset.amount.replace(/[,()]/g, "");
			const numValue = Number(cleanValue);

			if (isNaN(numValue)) return sum;

			// If the amount is in parentheses, treat it as negative
			return sum + (asset.amount.includes("(") ? -numValue : numValue);
		}, 0);
	};

	const calculateGrandTotal = () => {
		return calculateRevolvingTotal() + calculateNonRevolvingTotal();
	};

	// const [unit, setUnit] = React.useState(1);

	// News Function
	// Helper Function to format numbers with commas
	const formatNumber = (value: string | number) => {
		if (!value && value !== 0) return "";

		// Convert to string and remove existing formatting
		let stringValue = String(value).replace(/[,\s]/g, "");

		// Handle parentheses
		const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
		if (isNegative) {
			stringValue = stringValue.slice(1, -1);
		}

		// Split into integer and decimal parts
		const [integerPart, decimalPart] = stringValue.split(".");

		// Format integer part with commas
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

		// Combine with decimal part if it exists
		const formattedValue =
			decimalPart !== undefined
				? `${formattedInteger}.${decimalPart.slice(0, 2)}`
				: formattedInteger;

		return isNegative ? `(${formattedValue})` : formattedValue;
	};

	// Helper Function to parse a number, removing commas

	return (
		<section>
			<Card className="border-none">
				<CardHeader className="border-b mb-[32px]">
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/report/finance/company/finance-property",
							})
						}
						className="flex items-center gap-[4px]  mb-4"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>

					<CardTitle>ແກ້ໄຂລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {yearly}</CardTitle>
					<CardDescription>ແກ້ໄຂລາຍລະອຽດ</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit}>
						<Table>
							<TableHeader>
								<TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black">
									<TableHead>ຊັບສິນ</TableHead>
									<TableHead>ມູນຄ່າຍັງເຫຼືອ</TableHead>
									<TableHead>ໝາຍເຫດ</TableHead>
									<TableHead className="w-[100px]">ຈັດການ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{/* Revolving Assets */}
								<TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
									<TableCell colSpan={4}>ຊັບສິນໝູນວຽນ I</TableCell>
								</TableRow>
								{revolvingAssets.map((asset, index) => (
									<TableRow key={index} className="[&>*]:border">
										<TableCell className="flex gap-x-1 items-center">
											{index + 1}.
											<Input
												value={asset.name}
												onChange={(e) => {
													const newAssets = [...revolvingAssets];
													newAssets[index].name = e.target.value;
													setRevolvingAssets(newAssets);
												}}
												readOnly={index < 8}
											/>
										</TableCell>
										<TableCell>
											{/* <Input
												type="text"
												value={formatNumber(asset.amount)}
												onChange={(e) => {
													const rawValue = e.target.value.replace(
														/[^\d.-]/g,
														"",
													); // Remove all non-numeric chars except decimal and minus
													if (
														rawValue === "" ||
														/^-?\d*\.?\d*$/.test(rawValue)
													) {
														const newAssets = [...revolvingAssets];
														newAssets[index].amount =
															parseFormattedNumber(rawValue);
														setRevolvingAssets(newAssets);
													}
												}}
												className="text-right"
											/> */}

											<Input
												type="text"
												value={formatNumber(asset.amount)}
												onChange={(e) => {
													const value = e.target.value;
													const newAssets = [...revolvingAssets]; // or nonRevolvingAssets

													// Allow empty input
													if (!value) {
														newAssets[index].amount = "";
														setRevolvingAssets(newAssets);
														return;
													}

													// Remove existing formatting
													const rawValue = value.replace(/,/g, "");

													// Check if the input matches our pattern (including decimals and parentheses)
													if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
														newAssets[index].amount = rawValue;
														setRevolvingAssets(newAssets);
													}
												}}
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
										<TableCell>
											<Input
												value={asset.remark}
												onChange={(e) => {
													const newAssets = [...revolvingAssets];
													newAssets[index].remark = e.target.value;
													setRevolvingAssets(newAssets);
												}}
											/>
										</TableCell>
										{/* <TableCell>
											{index > 7 ? (
												<Button
													type="button"

													size="icon"
													onClick={() => removeRevolvingAsset(index)}
												>
													<Trash2 className="w-4 h-4 text-red-500" />
												</Button>
											) : null}
										</TableCell> */}
									</TableRow>
								))}
								{/* <Button
									type="button"

									size="icon"
									onClick={addRevolvingAsset}
									className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
								>
									<CirclePlus className="w-4 h-4" />
									ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
								</Button> */}
								<TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
									<TableCell>ລວມຊັບສິນໝູນວຽນ I</TableCell>
									<TableCell className="text-right">
										{formatNumber(calculateRevolvingTotal())}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
								{/* Non-Revolving Assets */}
								<TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
									<TableCell colSpan={4}>ຊັບສິນບໍ່ໝູນວຽນ II</TableCell>
								</TableRow>
								{nonRevolvingAssets.map((asset, index) => (
									<TableRow key={index} className="[&>*]:border">
										<TableCell className="flex gap-x-1 items-center">
											{index + 1}.
											<Input
												value={asset.name}
												onChange={(e) => {
													const newAssets = [...nonRevolvingAssets];
													newAssets[index].name = e.target.value;
													setNonRevolvingAssets(newAssets);
												}}
												readOnly={index < 10}
											/>
										</TableCell>
										<TableCell>
											{/* <Input
												type="text"
												value={formatNumber(asset.amount)}
												onChange={(e) => {
													const rawValue = e.target.value.replace(
														/[^\d.-]/g,
														"",
													); // Remove all non-numeric chars except decimal and minus
													if (
														rawValue === "" ||
														/^-?\d*\.?\d*$/.test(rawValue)
													) {
														const newAssets = [...nonRevolvingAssets];
														newAssets[index].amount =
															parseFormattedNumber(rawValue);
														setNonRevolvingAssets(newAssets);
													}
												}}
												className="text-right"
											/> */}

											<Input
												type="text"
												value={formatNumber(asset.amount)}
												onChange={(e) => {
													const value = e.target.value;
													const newAssets = [...nonRevolvingAssets]; // or nonRevolvingAssets

													// Allow empty input
													if (!value) {
														newAssets[index].amount = "";
														setNonRevolvingAssets(newAssets);
														return;
													}

													// Remove existing formatting
													const rawValue = value.replace(/,/g, "");

													// Check if the input matches our pattern (including decimals and parentheses)
													if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
														newAssets[index].amount = rawValue;
														setNonRevolvingAssets(newAssets);
													}
												}}
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
										<TableCell>
											<Input
												value={asset.remark}
												onChange={(e) => {
													const newAssets = [...nonRevolvingAssets];
													newAssets[index].remark = e.target.value;
													setNonRevolvingAssets(newAssets);
												}}
											/>
										</TableCell>
										{/* <TableCell>
											{index > 9 ? (
												<Button
													type="button"

													size="icon"
													onClick={() => removeNonRevolvingAsset(index)}
												>
													<Trash2 className="w-4 h-4 text-red-500" />
												</Button>
											) : null}
										</TableCell> */}
									</TableRow>
								))}
								{/* <Button
									type="button"

									size="icon"
									onClick={addNonRevolvingAsset}
									className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
								>
									<CirclePlus className="w-4 h-4" />
									ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
								</Button> */}
								<TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
									<TableCell>ລວມຊັບສິນບໍ່ໝູນວຽນ II</TableCell>
									<TableCell className="text-right">
										{formatNumber(calculateNonRevolvingTotal())}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
								<TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
									<TableCell>ລວມຊັບສິນ I + II</TableCell>
									<TableCell className="text-right">
										{formatNumber(calculateGrandTotal())}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
							</TableBody>
						</Table>
						{/* <div className="flex gap-x-1 items-center mt-4">
							<Label>ເອກະສານແນບ</Label>
							<Input
								type="file"
								value={docFile}
								onChange={(e) => setDocFile(e.target.value)}
								placeholder="ລິ້ງເອກະສານ"
								className="max-w-[300px]"
							/>
						</div> */}
						<div className="flex gap-x-1 items-center mt-4">
							{/* <Input
														type="file"
														value={docFile}
														onChange={(e) => setDocFile(e.target.value)}
														placeholder="ລິ້ງເອກະສານ"
														className="max-w-[300px]"
													/> */}

							<FileUpload
								currentFile={docFile}
								onFileUpload={(file) => {
									console.log(file, "File Uploaded");
									setDocFile(file);
								}}
							/>
						</div>
						<div className="flex gap-x-2 justify-end mt-4">
							<Button
								type="button"
								onClick={() =>
									navigate({
										to: "/report/finance",
										search: { page: 1, startYear: 0, endYear: 0 },
									})
								}
								variant={"outline"}
							>
								ຍົກເລີກ
							</Button>
							<Button
								disabled={mutation.isPending}
								type="submit"
								className="flex gap-x-2 items-center"
							>
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									<>
										<Save className="w-4 h-4" />
										ບັນທຶກ
									</>
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</section>
	);
}
